import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Progress } from "@/components/ui/progress";
import { GraduationCap, Eye, Car, ArrowRight, Trophy, PartyPopper, Clock, Check, Sparkles } from "lucide-react";

// v1.2 Part 1: Student Progress Card. Tone is intentionally encouraging
// rather than clinical — parents should immediately understand where
// their student stands, what's been accomplished, and what's next.
const encouragingMessage = (percent, roadTestEligible) => {
  if (roadTestEligible) return "Road test ready — what an incredible milestone!";
  if (percent >= 75) return "So close! Just a little more to go.";
  if (percent >= 50) return "Halfway there — great momentum!";
  if (percent >= 25) return "Building solid habits — keep it up!";
  if (percent > 0) return "Off to a great start!";
  return "Just getting started — every lesson counts.";
};

const ProgressMetric = ({ icon: Icon, label, completed, required, testId }) => {
  const pct = required > 0 ? Math.min((completed / required) * 100, 100) : 100;
  return (
    <div className="rounded-lg border p-2.5 space-y-1.5" data-testid={testId}>
      <Icon className="h-4 w-4 text-[hsl(var(--role-accent))]" />
      <p className="text-sm font-semibold tabular-nums">
        {completed}/{required}
      </p>
      <p className="text-[11px] text-muted-foreground leading-tight">{label}</p>
      <Progress value={pct} className="h-1" />
    </div>
  );
};

// Module-level constants (not recreated every render) — the parts of the
// milestone-pill animations that never vary per-item.
const MILESTONE_ACHIEVED_INITIAL = { opacity: 0, y: 4, scale: 0.98 };
const MILESTONE_ANIMATE = { opacity: 1, y: 0, scale: 1 };
const MILESTONE_SPARKLE_INITIAL = { scale: 0.6, opacity: 0 };
const MILESTONE_SPARKLE_ANIMATE = { scale: 1, opacity: 1 };
const MILESTONE_SPARKLE_TRANSITION = { duration: 0.2, delay: 0.25 };

export const StudentProgressCard = ({ student }) => {
  const p = student.progress;
  if (!p) return null;
  const estimate = p.estimated_completion;

  return (
    <section className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-4" data-testid="student-progress-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Student Progress</p>
          <h2 className="font-heading text-lg font-semibold">{student.full_name}</h2>
        </div>
        <Link
          to={`/students/${student.id}`}
          className="text-xs text-[hsl(var(--role-accent))] font-medium flex items-center gap-1 shrink-0 mt-1"
          data-testid="student-progress-view-link"
        >
          View details <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {p.road_test_eligible && (
        <div
          className="flex items-center gap-2 rounded-lg border border-[hsl(var(--status-good))]/30 bg-[hsl(var(--status-good))]/10 p-3"
          data-testid="road-test-ready-banner"
        >
          <Trophy className="h-5 w-5 text-[hsl(var(--status-good))] shrink-0" />
          <p className="text-sm font-medium text-[hsl(var(--status-good))]">
            Road Test Ready! All training requirements have been met.
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium" data-testid="student-progress-message">
            {encouragingMessage(p.overall_percent, p.road_test_eligible)}
          </span>
          <span className="text-muted-foreground tabular-nums" data-testid="student-progress-percent">
            {p.overall_percent}%
          </span>
        </div>
        <Progress value={p.overall_percent} className="h-2.5" data-testid="student-progress-bar" />
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <ProgressMetric icon={Car} label="Driving Lessons" completed={p.lessons_completed} required={p.lessons_required} testId="progress-metric-lessons" />
        <ProgressMetric icon={GraduationCap} label="Classroom Hrs" completed={p.classroom_hours_completed} required={p.classroom_hours_required} testId="progress-metric-classroom" />
        <ProgressMetric icon={Eye} label="Observation Hrs" completed={p.observation_hours_completed} required={p.observation_hours_required} testId="progress-metric-observation" />
      </div>

      {p.remaining_lessons > 0 ? (
        <div className="flex items-start gap-2 rounded-lg bg-secondary p-3" data-testid="estimated-completion-block">
          <Clock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <p className="text-sm font-medium">
              {p.remaining_lessons} lesson{p.remaining_lessons === 1 ? "" : "s"} remaining &middot; {estimate.label}
            </p>
            <p className="text-xs text-muted-foreground">
              This is an estimate based on your current scheduling pace — not a guaranteed date. Instructor
              availability, cancellations, and how often lessons are scheduled can all shift the actual timeline.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-lg bg-secondary p-3" data-testid="program-complete-block">
          <PartyPopper className="h-4 w-4 text-[hsl(var(--role-accent))] shrink-0" />
          <p className="text-sm font-medium">All training requirements complete!</p>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Milestones</p>
        <div className="flex flex-wrap gap-2" data-testid="milestones-row">
          {(() => {
            const lastAchievedIndex = p.milestones.reduce(
              (lastIdx, m, idx) => (m.achieved ? idx : lastIdx), -1
            );
            return p.milestones.map((m, idx) => {
              const isMostRecent = m.achieved && idx === lastAchievedIndex;
              return (
                <motion.span
                  key={m.id}
                  initial={m.achieved ? MILESTONE_ACHIEVED_INITIAL : false}
                  animate={MILESTONE_ANIMATE}
                  transition={{ duration: 0.2, delay: m.achieved ? idx * 0.04 : 0 }}
                  className={`relative inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 ${
                    m.achieved
                      ? "bg-[hsl(var(--role-accent)/0.12)] text-foreground border-[hsl(var(--role-accent)/0.25)] ring-1 ring-[hsl(var(--presentation)/0.25)] hover:bg-[hsl(var(--role-accent)/0.16)] hover:border-[hsl(var(--role-accent)/0.35)]"
                      : "bg-muted border-border text-muted-foreground/60"
                  }`}
                  data-testid={m.achieved ? `student-progress-milestone-pill-achieved-${m.id}` : `student-progress-milestone-pill-${m.id}`}
                  title={m.achieved ? `Achieved: ${m.label}` : `Not yet: ${m.label}`}
                >
                  {m.achieved && <Check className="h-3 w-3 shrink-0" aria-hidden="true" />}
                  <span aria-hidden="true">{m.emoji}</span> {m.label}
                  {isMostRecent && (
                    <motion.span
                      initial={MILESTONE_SPARKLE_INITIAL}
                      animate={MILESTONE_SPARKLE_ANIMATE}
                      transition={MILESTONE_SPARKLE_TRANSITION}
                      data-testid="student-progress-milestone-most-recent"
                    >
                      <Sparkles className="h-3 w-3 text-[hsl(var(--presentation))] shrink-0" aria-hidden="true" />
                    </motion.span>
                  )}
                </motion.span>
              );
            });
          })()}
        </div>
      </div>
    </section>
  );
};
